/**
 * PlatformLeadDrawer — sales-focused drawer opened when a Platform pipeline
 * card is clicked. Replaces the tenant HVAC-style CustomerProfileOverlay for
 * platform sales cards (which are prospects/leads, not tenant customers).
 *
 * Seven tabs: Overview · Activity · Emails · Appointments · Reminders · Notes · Forms
 * All tabs stream from the shared `/api/pipeline/cards/{cardId}/*` endpoints.
 */
import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  X, Building2, User as UserIcon, Mail, Phone, Calendar, Bell, StickyNote,
  Activity as ActivityIcon, FileText, ExternalLink, Loader2, Plus, Trash2,
  Check, Star, Zap, Send, MessageSquare, ClipboardList, Users, DollarSign,
  ArrowUpRight,
} from "lucide-react";
import { toast } from "sonner";
import { pipelineApi, pipelineLeadApi, usersApi } from "../../lib/api";

const INDUSTRIES = ["HVAC", "Plumbing", "Electrical", "Roofing", "Landscaping", "Pest Control", "Other"];
const SIZES = ["1-5 techs", "6-20 techs", "21-50 techs", "50+ techs"];
const PLANS = ["Starter", "Pro", "Enterprise"];

const ACTIVITY_META = {
  note:                 { icon: StickyNote,     color: "text-slate-300", label: "Note" },
  email_sent:           { icon: Mail,           color: "text-sky-300",   label: "Email sent" },
  email_received:       { icon: Mail,           color: "text-emerald-300", label: "Email received" },
  sms_sent:             { icon: MessageSquare,  color: "text-sky-300",   label: "SMS sent" },
  sms_received:         { icon: MessageSquare,  color: "text-emerald-300", label: "SMS received" },
  automation:           { icon: Zap,            color: "text-amber-300", label: "Automation" },
  form_submitted:       { icon: ClipboardList,  color: "text-purple-300", label: "Form submitted" },
  stage_moved:          { icon: ArrowUpRight,   color: "text-indigo-300", label: "Stage moved" },
  assigned:             { icon: Users,          color: "text-indigo-300", label: "Assigned" },
  appointment_scheduled:{ icon: Calendar,       color: "text-blue-300",  label: "Appointment" },
  reminder_created:     { icon: Bell,           color: "text-rose-300",  label: "Reminder set" },
  reminder_fired:       { icon: Bell,           color: "text-rose-400",  label: "Reminder fired" },
  status:               { icon: Check,          color: "text-emerald-300", label: "Status" },
};

const fmtDT = (iso) => {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleString([], { dateStyle: "medium", timeStyle: "short" }); } catch { return iso; }
};
const fmtRel = (iso) => {
  if (!iso) return "";
  try {
    const d = new Date(iso), now = new Date();
    const diff = Math.round((d - now) / 60000);
    if (Math.abs(diff) < 60) return `${diff >= 0 ? "in" : ""} ${Math.abs(diff)}m ${diff < 0 ? "ago" : ""}`.trim();
    const h = Math.round(diff / 60);
    if (Math.abs(h) < 48) return `${h >= 0 ? "in" : ""} ${Math.abs(h)}h ${h < 0 ? "ago" : ""}`.trim();
    return d.toLocaleDateString([], { month: "short", day: "numeric" });
  } catch { return ""; }
};

const TABS = [
  ["overview",     "Overview",     Building2],
  ["activity",     "Activity",     ActivityIcon],
  ["emails",       "Emails",       Mail],
  ["appointments", "Appointments", Calendar],
  ["reminders",    "Reminders",    Bell],
  ["notes",        "Notes",        StickyNote],
  ["forms",        "Forms",        ClipboardList],
];

export default function PlatformLeadDrawer({ cardId, stages = [], onClose, onCardUpdated, onCardDeleted }) {
  const [tab, setTab] = useState("overview");
  const [card, setCard] = useState(null);
  const [loading, setLoading] = useState(false);
  const [linked, setLinked] = useState(null);

  const load = useCallback(async () => {
    if (!cardId) return;
    setLoading(true);
    try {
      // Pull ALL cards for the platform pipeline and find ours.
      const boardId = "platform-sales";
      const cards = await pipelineApi.cards(boardId);
      const c = cards.find((x) => x.id === cardId);
      if (!c) { toast.error("Card not found"); onClose?.(); return; }
      setCard(c);
      const lu = await pipelineLeadApi.linkedUser(cardId).catch(() => null);
      setLinked(lu);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not load card");
    } finally { setLoading(false); }
  }, [cardId, onClose]);

  useEffect(() => { load(); }, [load]);

  const save = async (patch) => {
    try {
      const updated = await pipelineApi.updateCard(cardId, patch);
      setCard(updated);
      onCardUpdated?.(updated);
      // Refresh linked-user in case contactEmail changed.
      pipelineLeadApi.linkedUser(cardId).then(setLinked).catch(() => {});
      toast.success("Saved");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    }
  };

  const remove = async () => {
    if (!window.confirm("Delete this lead card? This cannot be undone.")) return;
    try {
      await pipelineApi.deleteCard(cardId);
      onCardDeleted?.(cardId);
      onClose?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Delete failed");
    }
  };

  if (!cardId) return null;

  return (
    <div className="fixed inset-0 z-[70] bg-black/60 flex justify-end" onClick={onClose} data-testid="lead-drawer-backdrop">
      <div className="w-full md:max-w-[820px] h-full bg-[#0B1730] text-slate-100 shadow-2xl overflow-hidden flex flex-col" onClick={(e) => e.stopPropagation()} data-testid="lead-drawer">
        {/* Header */}
        <div className="px-5 py-4 border-b border-white/10 flex items-center gap-3 shrink-0">
          <span className="h-10 w-10 rounded-xl bg-blue-500/20 text-blue-200 flex items-center justify-center font-bold text-[15px]">
            {(card?.companyName || card?.title || "?").slice(0, 2).toUpperCase()}
          </span>
          <div className="flex-1 min-w-0">
            <div className="text-[15px] font-bold truncate flex items-center gap-2" data-testid="lead-drawer-title">
              {card?.title || "Loading…"}
              {linked?.isCurrentUser && (
                <span data-testid="lead-drawer-current-user" className="inline-flex items-center gap-1 px-2 h-5 rounded-full bg-emerald-500/20 border border-emerald-400/40 text-emerald-300 text-[10px] font-bold uppercase tracking-wide">
                  <UserIcon className="h-2.5 w-2.5" /> Current user
                </span>
              )}
            </div>
            <div className="text-[11.5px] text-slate-400 truncate">
              {card?.companyName ? `${card.companyName} · ` : ""}
              {card?.contactName || "No contact"} · {card?.stageId ? (stages.find((s) => s.id === card.stageId)?.name || "Stage") : ""}
            </div>
          </div>
          <button onClick={remove} data-testid="lead-drawer-delete" title="Delete card" className="h-8 w-8 rounded-md hover:bg-rose-500/20 text-rose-300 flex items-center justify-center"><Trash2 className="h-4 w-4" /></button>
          <button onClick={onClose} data-testid="lead-drawer-close" className="h-8 w-8 rounded-md hover:bg-white/10 text-slate-300 flex items-center justify-center"><X className="h-4 w-4" /></button>
        </div>

        <div className="flex-1 min-h-0 flex overflow-hidden">
          {/* Left tab rail */}
          <nav className="w-48 shrink-0 border-r border-white/10 bg-[#0A1226] py-3 overflow-y-auto" data-testid="lead-drawer-tabs">
            {TABS.map(([key, label, Icon]) => (
              <button
                key={key}
                data-testid={`lead-drawer-tab-${key}`}
                onClick={() => setTab(key)}
                className={`w-full flex items-center gap-2.5 px-4 py-2.5 text-[13px] transition-colors ${
                  tab === key ? "bg-blue-500/15 text-blue-300 border-r-2 border-blue-400 font-semibold" : "text-slate-400 hover:text-slate-200 hover:bg-white/5"
                }`}
              >
                <Icon className="h-4 w-4" /> {label}
              </button>
            ))}
          </nav>

          {/* Right content pane */}
          <div className="flex-1 min-w-0 overflow-y-auto">
            {loading || !card ? (
              <div className="h-full flex items-center justify-center text-slate-400"><Loader2 className="h-5 w-5 animate-spin" /></div>
            ) : (
              <div className="p-5 md:p-6">
                {tab === "overview"     && <OverviewTab card={card} stages={stages} onSave={save} />}
                {tab === "activity"     && <ActivityTab cardId={cardId} />}
                {tab === "emails"       && <EmailsTab cardId={cardId} card={card} />}
                {tab === "appointments" && <AppointmentsTab cardId={cardId} onRefresh={load} />}
                {tab === "reminders"    && <RemindersTab cardId={cardId} onRefresh={load} />}
                {tab === "notes"        && <NotesTab cardId={cardId} />}
                {tab === "forms"        && <FormsTab cardId={cardId} card={card} />}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}


/* --------------------------- Overview tab --------------------------- */

function OverviewTab({ card, stages, onSave }) {
  const [f, setF] = useState({
    companyName:  card.companyName || "",
    contactName:  card.contactName || "",
    contactEmail: card.contactEmail || "",
    contactPhone: card.contactPhone || "",
    industry:     card.industry || "",
    companySize:  card.companySize || "",
    planInterest: card.planInterest || "",
    value:        card.value || 0,
    trialStartsAt:   card.trialStartsAt || "",
    expectedCloseAt: card.expectedCloseAt || "",
    stageId:      card.stageId,
    assigneeId:   card.assigneeId || "",
    source:       card.source || "Manual",
    tags:         (card.tags || []).join(", "),
  });
  const [assignees, setAssignees] = useState([]);
  useEffect(() => { usersApi.list().then(setAssignees).catch(() => setAssignees([])); }, []);

  const dirty = useMemo(() => {
    const cur = {
      ...f, tags: f.tags.split(",").map((t) => t.trim()).filter(Boolean).join(","),
    };
    const orig = {
      companyName: card.companyName || "", contactName: card.contactName || "",
      contactEmail: card.contactEmail || "", contactPhone: card.contactPhone || "",
      industry: card.industry || "", companySize: card.companySize || "",
      planInterest: card.planInterest || "", value: card.value || 0,
      trialStartsAt: card.trialStartsAt || "", expectedCloseAt: card.expectedCloseAt || "",
      stageId: card.stageId, assigneeId: card.assigneeId || "", source: card.source || "Manual",
      tags: (card.tags || []).join(","),
    };
    return JSON.stringify(cur) !== JSON.stringify(orig);
  }, [f, card]);

  const submit = () => {
    const patch = {
      ...f,
      value: parseFloat(f.value) || 0,
      tags: f.tags.split(",").map((t) => t.trim()).filter(Boolean),
      assigneeId: f.assigneeId || null,
    };
    onSave(patch);
  };

  return (
    <div className="space-y-5">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <Stat label="Deal value"   value={`$${Number(f.value || 0).toLocaleString()}`} icon={DollarSign} />
        <Stat label="Plan interest" value={f.planInterest || "—"} icon={Star} />
        <Stat label="Expected close" value={fmtDT(f.expectedCloseAt).replace(",", " ·")} icon={Calendar} />
      </div>

      <Section title="Contact">
        <Grid>
          <Field label="Company"><Input value={f.companyName} onChange={(v) => setF({ ...f, companyName: v })} data-testid="lead-overview-company" placeholder="Acme HVAC" /></Field>
          <Field label="Contact name"><Input value={f.contactName} onChange={(v) => setF({ ...f, contactName: v })} data-testid="lead-overview-contact-name" placeholder="Jordan Lee" /></Field>
          <Field label="Email"><Input value={f.contactEmail} onChange={(v) => setF({ ...f, contactEmail: v })} data-testid="lead-overview-contact-email" placeholder="jordan@acme.com" /></Field>
          <Field label="Phone"><Input value={f.contactPhone} onChange={(v) => setF({ ...f, contactPhone: v })} data-testid="lead-overview-contact-phone" placeholder="+1 555 000 0000" /></Field>
        </Grid>
      </Section>

      <Section title="Business">
        <Grid>
          <Field label="Industry">
            <Select value={f.industry} onChange={(v) => setF({ ...f, industry: v })} data-testid="lead-overview-industry">
              <option value="">—</option>
              {INDUSTRIES.map((i) => <option key={i} value={i}>{i}</option>)}
            </Select>
          </Field>
          <Field label="Company size">
            <Select value={f.companySize} onChange={(v) => setF({ ...f, companySize: v })} data-testid="lead-overview-size">
              <option value="">—</option>
              {SIZES.map((s) => <option key={s} value={s}>{s}</option>)}
            </Select>
          </Field>
          <Field label="Plan interest">
            <Select value={f.planInterest} onChange={(v) => setF({ ...f, planInterest: v })} data-testid="lead-overview-plan">
              <option value="">—</option>
              {PLANS.map((p) => <option key={p} value={p}>{p}</option>)}
            </Select>
          </Field>
          <Field label="Source">
            <Input value={f.source} onChange={(v) => setF({ ...f, source: v })} data-testid="lead-overview-source" />
          </Field>
        </Grid>
      </Section>

      <Section title="Deal">
        <Grid>
          <Field label="Deal value ($)">
            <Input type="number" value={f.value} onChange={(v) => setF({ ...f, value: v })} data-testid="lead-overview-value" />
          </Field>
          <Field label="Stage">
            <Select value={f.stageId} onChange={(v) => setF({ ...f, stageId: v })} data-testid="lead-overview-stage">
              {stages.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
            </Select>
          </Field>
          <Field label="Trial starts">
            <Input type="date" value={(f.trialStartsAt || "").slice(0, 10)} onChange={(v) => setF({ ...f, trialStartsAt: v })} data-testid="lead-overview-trial-start" />
          </Field>
          <Field label="Expected close">
            <Input type="date" value={(f.expectedCloseAt || "").slice(0, 10)} onChange={(v) => setF({ ...f, expectedCloseAt: v })} data-testid="lead-overview-close" />
          </Field>
          <Field label="Assignee">
            <Select value={f.assigneeId} onChange={(v) => setF({ ...f, assigneeId: v })} data-testid="lead-overview-assignee">
              <option value="">Unassigned</option>
              {assignees.map((u) => <option key={u.id} value={u.id}>{u.name || u.email}</option>)}
            </Select>
          </Field>
          <Field label="Tags (comma-separated)">
            <Input value={f.tags} onChange={(v) => setF({ ...f, tags: v })} data-testid="lead-overview-tags" placeholder="VIP, hot" />
          </Field>
        </Grid>
      </Section>

      <div className="flex justify-end">
        <button
          disabled={!dirty}
          onClick={submit}
          data-testid="lead-overview-save"
          className="h-9 px-4 rounded-md bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-40 text-[13px] font-semibold"
        >
          Save changes
        </button>
      </div>
    </div>
  );
}


/* --------------------------- Activity tab --------------------------- */

function ActivityTab({ cardId }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  useEffect(() => {
    let live = true;
    setLoading(true);
    pipelineLeadApi.activities(cardId).then((r) => { if (live) setRows(r); }).finally(() => { if (live) setLoading(false); });
    return () => { live = false; };
  }, [cardId]);

  if (loading) return <Loader />;
  if (rows.length === 0) return <Empty icon={ActivityIcon} text="No activity yet. Start a conversation or set a reminder." />;
  return (
    <ul className="space-y-2" data-testid="lead-activity-list">
      {rows.map((a) => {
        const meta = ACTIVITY_META[a.type] || { icon: ActivityIcon, color: "text-slate-300", label: a.type };
        const I = meta.icon;
        return (
          <li key={a.id} data-testid={`lead-activity-${a.id}`} className="flex gap-3 p-3 rounded-lg bg-white/5 border border-white/10">
            <span className={`h-8 w-8 shrink-0 rounded-full bg-white/10 ${meta.color} flex items-center justify-center`}><I className="h-4 w-4" /></span>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2">
                <div className="text-[13px] font-semibold truncate">{a.title}</div>
                <span className="text-[10px] text-slate-500 uppercase tracking-wide">{meta.label}</span>
              </div>
              {a.body && <div className="text-[12.5px] text-slate-400 mt-0.5 whitespace-pre-wrap">{a.body}</div>}
              <div className="text-[11px] text-slate-500 mt-1">
                {a.createdByName || "System"} · {fmtDT(a.createdAt)}
              </div>
            </div>
          </li>
        );
      })}
    </ul>
  );
}


/* --------------------------- Emails tab --------------------------- */

function EmailsTab({ cardId, card }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  useEffect(() => {
    let live = true;
    setLoading(true);
    pipelineLeadApi.emails(cardId).then((r) => { if (live) setRows(r); }).finally(() => { if (live) setLoading(false); });
    return () => { live = false; };
  }, [cardId]);
  if (!card?.contactEmail) return <Empty icon={Mail} text="Add a contact email in the Overview tab to auto-link email conversations." />;
  if (loading) return <Loader />;
  if (rows.length === 0) return <Empty icon={Mail} text={`No emails yet with ${card.contactEmail}.`} />;
  return (
    <ul className="space-y-2" data-testid="lead-emails-list">
      {rows.map((c) => (
        <li key={c.id} data-testid={`lead-email-conv-${c.id}`} className="p-3 rounded-lg bg-white/5 border border-white/10 hover:bg-white/10 transition-colors">
          <a href={c.link} className="flex items-start gap-3">
            <span className="h-8 w-8 shrink-0 rounded-full bg-sky-500/20 text-sky-300 flex items-center justify-center"><Mail className="h-4 w-4" /></span>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2">
                <div className="text-[13px] font-semibold truncate">{c.contactName || c.contactEmail}</div>
                {c.unread > 0 && <span className="min-w-[16px] h-4 px-1 rounded-full bg-rose-500 text-white text-[9px] font-bold flex items-center justify-center">{c.unread}</span>}
              </div>
              <div className="text-[12px] text-slate-400 mt-0.5 truncate">{c.lastMessagePreview || "No messages"}</div>
              <div className="text-[11px] text-slate-500 mt-1">{fmtDT(c.lastMessageAt)}</div>
            </div>
            <ExternalLink className="h-4 w-4 text-slate-500 shrink-0" />
          </a>
        </li>
      ))}
    </ul>
  );
}


/* --------------------------- Appointments tab --------------------------- */

function AppointmentsTab({ cardId }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [creating, setCreating] = useState(false);
  const [title, setTitle] = useState("");
  const [date, setDate] = useState("");
  const [time, setTime] = useState("");
  const [agenda, setAgenda] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try { setRows(await pipelineLeadApi.appointments(cardId)); }
    finally { setLoading(false); }
  }, [cardId]);
  useEffect(() => { load(); }, [load]);

  const add = async () => {
    if (!title.trim() || !date || !time) { toast.error("Title, date and time are required"); return; }
    setCreating(true);
    try {
      await pipelineLeadApi.createAppointment(cardId, { title: title.trim(), startsAt: `${date}T${time}`, notes: agenda });
      setTitle(""); setDate(""); setTime(""); setAgenda("");
      await load();
      toast.success("Appointment scheduled — also added to the Platform Console Calendar");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not schedule");
    } finally { setCreating(false); }
  };

  const del = async (id) => {
    if (!window.confirm("Delete this appointment?")) return;
    try { await pipelineLeadApi.deleteAppointment(cardId, id); await load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Delete failed"); }
  };

  return (
    <div className="space-y-4">
      <Section title="Schedule an appointment" subtitle="Also shows up automatically on the Platform Console Calendar.">
        <div className="space-y-2">
          <Input value={title} onChange={setTitle} data-testid="lead-appt-title" placeholder="Discovery call, product demo, contract signing…" />
          <div className="grid grid-cols-2 gap-2">
            <Input type="date" value={date} onChange={setDate} data-testid="lead-appt-date" />
            <Input type="time" value={time} onChange={setTime} data-testid="lead-appt-time" />
          </div>
          <Input value={agenda} onChange={setAgenda} data-testid="lead-appt-notes" placeholder="Agenda for this appointment…" />
          <div className="flex justify-end">
            <button
              onClick={add}
              disabled={creating || !title.trim() || !date || !time}
              data-testid="lead-appt-create"
              className="h-9 px-4 rounded-md bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-40 text-[13px] font-semibold flex items-center gap-1.5"
            >
              {creating ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />} Schedule
            </button>
          </div>
        </div>
      </Section>

      <Section title="Upcoming & past">
        {loading ? <Loader /> : rows.length === 0 ? (
          <Empty icon={Calendar} text="No appointments yet." small />
        ) : (
          <ul className="space-y-2" data-testid="lead-appt-list">
            {rows.map((a) => (
              <li key={a.id} data-testid={`lead-appt-${a.id}`} className="flex items-start gap-3 p-3 rounded-lg bg-white/5 border border-white/10">
                <span className="h-8 w-8 shrink-0 rounded-full bg-blue-500/20 text-blue-300 flex items-center justify-center"><Calendar className="h-4 w-4" /></span>
                <div className="flex-1 min-w-0">
                  <div className="text-[13px] font-semibold">{a.title}</div>
                  <div className="text-[11.5px] text-slate-400">{fmtDT(a.startsAt)} · {fmtRel(a.startsAt)}</div>
                  {a.notes && <div className="text-[12px] text-slate-400 mt-1">{a.notes}</div>}
                </div>
                <button onClick={() => del(a.id)} data-testid={`lead-appt-delete-${a.id}`} title="Delete" className="h-7 w-7 rounded-md hover:bg-rose-500/20 text-rose-300 flex items-center justify-center"><Trash2 className="h-3.5 w-3.5" /></button>
              </li>
            ))}
          </ul>
        )}
      </Section>
    </div>
  );
}


/* --------------------------- Reminders tab --------------------------- */

function RemindersTab({ cardId }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [title, setTitle] = useState("");
  const [dueAt, setDueAt] = useState("");
  const [notes, setNotes] = useState("");
  const [creating, setCreating] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try { setRows(await pipelineLeadApi.reminders(cardId)); }
    finally { setLoading(false); }
  }, [cardId]);
  useEffect(() => { load(); }, [load]);

  const add = async () => {
    if (!title.trim() || !dueAt) { toast.error("Title and due date/time are required"); return; }
    setCreating(true);
    try {
      await pipelineLeadApi.createReminder(cardId, { title: title.trim(), dueAt, notes });
      setTitle(""); setDueAt(""); setNotes("");
      await load();
      toast.success("Reminder set — you'll get a bell notification when it's due.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not create reminder");
    } finally { setCreating(false); }
  };

  const done = async (id) => {
    try { await pipelineLeadApi.updateReminder(cardId, id, { status: "done" }); await load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Update failed"); }
  };
  const del = async (id) => {
    if (!window.confirm("Delete this reminder?")) return;
    try { await pipelineLeadApi.deleteReminder(cardId, id); await load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Delete failed"); }
  };

  return (
    <div className="space-y-4">
      <Section title="Add a reminder" subtitle="You'll get an in-app notification when it fires.">
        <div className="space-y-2">
          <Input value={title} onChange={setTitle} data-testid="lead-rem-title" placeholder="Follow up on contract, send pricing…" />
          <div className="grid grid-cols-2 gap-2">
            <Input type="datetime-local" value={dueAt} onChange={setDueAt} data-testid="lead-rem-due" />
            <Input value={notes} onChange={setNotes} data-testid="lead-rem-notes" placeholder="Notes (optional)" />
          </div>
          <div className="flex justify-end">
            <button
              onClick={add}
              disabled={creating || !title.trim() || !dueAt}
              data-testid="lead-rem-create"
              className="h-9 px-4 rounded-md bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-40 text-[13px] font-semibold flex items-center gap-1.5"
            >
              {creating ? <Loader2 className="h-4 w-4 animate-spin" /> : <Bell className="h-4 w-4" />} Set reminder
            </button>
          </div>
        </div>
      </Section>

      <Section title="Reminders">
        {loading ? <Loader /> : rows.length === 0 ? (
          <Empty icon={Bell} text="No reminders yet." small />
        ) : (
          <ul className="space-y-2" data-testid="lead-rem-list">
            {rows.map((r) => (
              <li key={r.id} data-testid={`lead-rem-${r.id}`} className={`flex items-start gap-3 p-3 rounded-lg border ${r.status === "done" ? "bg-emerald-500/5 border-emerald-500/20 opacity-70" : r.status === "fired" ? "bg-rose-500/10 border-rose-500/30" : "bg-white/5 border-white/10"}`}>
                <span className={`h-8 w-8 shrink-0 rounded-full flex items-center justify-center ${r.status === "fired" ? "bg-rose-500/20 text-rose-300" : "bg-white/10 text-slate-300"}`}><Bell className="h-4 w-4" /></span>
                <div className="flex-1 min-w-0">
                  <div className={`text-[13px] font-semibold ${r.status === "done" ? "line-through text-slate-400" : ""}`}>{r.title}</div>
                  <div className="text-[11.5px] text-slate-400">Due {fmtDT(r.dueAt)} · {fmtRel(r.dueAt)}</div>
                  {r.notes && <div className="text-[12px] text-slate-400 mt-1">{r.notes}</div>}
                </div>
                {r.status !== "done" && (
                  <button onClick={() => done(r.id)} data-testid={`lead-rem-done-${r.id}`} title="Mark done" className="h-7 w-7 rounded-md hover:bg-emerald-500/20 text-emerald-300 flex items-center justify-center"><Check className="h-3.5 w-3.5" /></button>
                )}
                <button onClick={() => del(r.id)} data-testid={`lead-rem-delete-${r.id}`} title="Delete" className="h-7 w-7 rounded-md hover:bg-rose-500/20 text-rose-300 flex items-center justify-center"><Trash2 className="h-3.5 w-3.5" /></button>
              </li>
            ))}
          </ul>
        )}
      </Section>
    </div>
  );
}


/* --------------------------- Notes tab --------------------------- */

function NotesTab({ cardId }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [body, setBody] = useState("");
  const [creating, setCreating] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const all = await pipelineLeadApi.activities(cardId);
      setRows(all.filter((a) => a.type === "note"));
    } finally { setLoading(false); }
  }, [cardId]);
  useEffect(() => { load(); }, [load]);

  const add = async () => {
    if (!body.trim()) return;
    setCreating(true);
    try {
      await pipelineLeadApi.addActivity(cardId, { type: "note", title: body.trim().slice(0, 60), body: body.trim() });
      setBody("");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not add note");
    } finally { setCreating(false); }
  };

  return (
    <div className="space-y-4">
      <Section title="Add a note">
        <textarea
          rows={3}
          value={body}
          onChange={(e) => setBody(e.target.value)}
          data-testid="lead-note-input"
          placeholder="Add context, next steps, mentions…"
          className="w-full resize-none rounded-md bg-[#16203A] border border-white/10 px-3 py-2 text-[13.5px] text-slate-100 placeholder:text-slate-500 focus:outline-none focus:ring-2 focus:ring-blue-500/30"
        />
        <div className="flex justify-end mt-2">
          <button
            onClick={add}
            disabled={creating || !body.trim()}
            data-testid="lead-note-create"
            className="h-9 px-4 rounded-md bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-40 text-[13px] font-semibold flex items-center gap-1.5"
          >
            {creating ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />} Add note
          </button>
        </div>
      </Section>

      {loading ? <Loader /> : rows.length === 0 ? (
        <Empty icon={StickyNote} text="No notes yet." small />
      ) : (
        <ul className="space-y-2" data-testid="lead-notes-list">
          {rows.map((n) => (
            <li key={n.id} data-testid={`lead-note-${n.id}`} className="p-3 rounded-lg bg-amber-500/5 border border-amber-500/20">
              <div className="text-[13px] text-slate-200 whitespace-pre-wrap">{n.body}</div>
              <div className="text-[10.5px] text-slate-500 mt-1.5">{n.createdByName || "You"} · {fmtDT(n.createdAt)}</div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}


/* --------------------------- Forms tab --------------------------- */

function FormsTab({ cardId, card }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  useEffect(() => {
    let live = true;
    setLoading(true);
    pipelineLeadApi.forms(cardId).then((r) => { if (live) setRows(r); }).finally(() => { if (live) setLoading(false); });
    return () => { live = false; };
  }, [cardId]);
  if (!card?.contactEmail) return <Empty icon={ClipboardList} text="Add a contact email in the Overview tab to see form submissions." />;
  if (loading) return <Loader />;
  if (rows.length === 0) return <Empty icon={ClipboardList} text={`No form submissions yet from ${card.contactEmail}.`} />;
  return (
    <ul className="space-y-2" data-testid="lead-forms-list">
      {rows.map((f, i) => (
        <li key={f.id || i} data-testid={`lead-form-${f.id || i}`} className="p-3 rounded-lg bg-white/5 border border-white/10">
          <div className="flex items-center gap-2 mb-1">
            <ClipboardList className="h-4 w-4 text-purple-300" />
            <div className="text-[13px] font-semibold">{f.formType === "demo_request" ? "Demo request" : f.formName || "Marketing form"}</div>
            <span className="ml-auto text-[11px] text-slate-500">{fmtDT(f.createdAt)}</span>
          </div>
          <dl className="grid grid-cols-2 gap-x-4 gap-y-1 text-[12px] mt-2">
            {Object.entries(f).filter(([k]) => !["_id", "id", "formType"].includes(k)).map(([k, v]) => (
              <React.Fragment key={k}>
                <dt className="text-slate-500">{k}</dt>
                <dd className="text-slate-200 truncate">{typeof v === "object" ? JSON.stringify(v) : String(v || "—")}</dd>
              </React.Fragment>
            ))}
          </dl>
        </li>
      ))}
    </ul>
  );
}


/* --------------------------- Small primitives --------------------------- */

const Loader = () => <div className="p-8 flex justify-center text-slate-400"><Loader2 className="h-5 w-5 animate-spin" /></div>;
const Empty = ({ icon: Icon, text, small = false }) => (
  <div className={`text-center text-slate-500 py-${small ? "6" : "12"}`}>
    <Icon className="h-8 w-8 mx-auto mb-2 opacity-40" />
    <div className="text-[13px]">{text}</div>
  </div>
);
const Section = ({ title, subtitle, children }) => (
  <section className="space-y-2">
    <div>
      <h3 className="text-[12px] font-bold uppercase tracking-wide text-slate-300">{title}</h3>
      {subtitle && <p className="text-[11.5px] text-slate-500 mt-0.5">{subtitle}</p>}
    </div>
    <div className="rounded-lg bg-[#0A1226] border border-white/10 p-4">{children}</div>
  </section>
);
const Grid = ({ children }) => <div className="grid grid-cols-1 md:grid-cols-2 gap-3">{children}</div>;
const Field = ({ label, children }) => (
  <label className="block">
    <span className="text-[11.5px] font-semibold text-slate-400 uppercase tracking-wide">{label}</span>
    <div className="mt-1">{children}</div>
  </label>
);
const Input = ({ value, onChange, type = "text", ...rest }) => (
  <input
    type={type}
    value={value ?? ""}
    onChange={(e) => onChange(e.target.value)}
    className="w-full h-9 px-3 rounded-md bg-[#16203A] border border-white/10 text-slate-100 placeholder:text-slate-500 text-[13px] focus:outline-none focus:ring-2 focus:ring-blue-500/30"
    {...rest}
  />
);
const Select = ({ value, onChange, children, ...rest }) => (
  <select
    value={value ?? ""}
    onChange={(e) => onChange(e.target.value)}
    className="w-full h-9 px-2.5 rounded-md bg-[#16203A] border border-white/10 text-slate-100 text-[13px] focus:outline-none focus:ring-2 focus:ring-blue-500/30"
    {...rest}
  >
    {children}
  </select>
);
const Stat = ({ label, value, icon: Icon }) => (
  <div className="rounded-lg bg-[#0A1226] border border-white/10 p-3">
    <div className="text-[10.5px] font-bold uppercase tracking-wide text-slate-500 flex items-center gap-1"><Icon className="h-3 w-3" /> {label}</div>
    <div className="text-[15px] font-bold text-slate-100 mt-1 truncate">{value}</div>
  </div>
);
